import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function RWD_Images() {
  return (
    <div className="tutorial-container">
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #f472b6, #d946ef)",
          color: "white",
          padding: "2rem",
          borderRadius: "16px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
          🖼️ Responsive Images (RWD)
        </h1>
        <p style={{ fontSize: "1.2rem", marginTop: "1rem" }}>
          Responsive images automatically adjust to different screen sizes,
          preserving aspect ratio and layout.
        </p>
        <img
          src="https://www.w3schools.com/css/img_lights.jpg"
          alt="Responsive Images Example"
          style={{
            maxWidth: "500px",
            margin: "1.5rem auto 0",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        />
      </div>

      {/* Intro */}
      <p className="tutorial-intro">
        Responsive images are crucial for creating flexible and fast-loading
        websites. Using CSS properties like <code>max-width</code>,{" "}
        <code>height: auto</code>, and modern HTML attributes such as{" "}
        <code>srcset</code> and <code>sizes</code>, you can ensure images scale
        correctly across devices.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fde68a, #facc15)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#78350f" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>max-width: 100%</code> and <code>height: auto</code> to
            scale images.
          </li>
          <li>
            <code>object-fit</code> helps maintain aspect ratio when cropping or
            fitting images.
          </li>
          <li>
            HTML <code>srcset</code> and <code>sizes</code> allow loading
            different images based on screen resolution.
          </li>
          <li>Responsive images improve page speed and UX on all devices.</li>
          <li>Combine with Flexbox or Grid to create responsive galleries.</li>
        </ul>
      </div>

      {/* Example 1: Basic Responsive Image */}
      <h2 className="section-title">1️⃣ Basic Responsive Image</h2>
      <CodeCopyBlock
        code={`img {
  max-width: 100%;
  height: auto;
}`}
      />
      <p>
        ✅ The image scales automatically to fit its container without
        distortion.
      </p>
      <img
        src="https://picsum.photos/id/1015/600/400"
        alt="Example 1"
        style={{
          maxWidth: "100%",
          height: "auto",
          borderRadius: "8px",
          marginBottom: "1.5rem",
        }}
      />

      {/* Example 2: Object Fit */}
      <h2 className="section-title">2️⃣ Object-Fit Example</h2>
      <CodeCopyBlock
        code={`.img-container {
  width: 300px;
  height: 200px;
  overflow: hidden;
}
.img-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}`}
      />
      <p>
        ✅ Maintains aspect ratio while filling the container, cropping if
        necessary.
      </p>
      <div
        style={{
          width: "300px",
          height: "200px",
          overflow: "hidden",
          marginBottom: "1.5rem",
        }}
      >
        <img
          src="https://picsum.photos/id/1025/600/400"
          alt="Example 2"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>

      {/* Example 3: Srcset & Sizes */}
      <h2 className="section-title">3️⃣ Responsive Srcset Example</h2>
      <CodeCopyBlock
        code={`<img 
  src="small.jpg" 
  srcset="medium.jpg 600w, large.jpg 1200w" 
  sizes="(max-width: 600px) 100vw, 50vw" 
  alt="Responsive Image Example" />`}
      />
      <p>
        ✅ Loads different image files depending on screen width for better
        performance.
      </p>

      {/* Example 4: Image Gallery */}
      <h2 className="section-title">4️⃣ Responsive Image Gallery</h2>
      <CodeCopyBlock
        code={`.gallery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}
.gallery img {
  width: 100%;
  height: auto;
  border-radius: 8px;
}`}
      />
      <p>
        ✅ Creates a flexible image grid that adjusts automatically to screen
        size.
      </p>
      <div
        className="gallery"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <img
          src="https://picsum.photos/id/1011/300/200"
          alt="Gallery 1"
          style={{ width: "100%", height: "auto", borderRadius: "8px" }}
        />
        <img
          src="https://picsum.photos/id/1015/300/200"
          alt="Gallery 2"
          style={{ width: "100%", height: "auto", borderRadius: "8px" }}
        />
        <img
          src="https://picsum.photos/id/1016/300/200"
          alt="Gallery 3"
          style={{ width: "100%", height: "auto", borderRadius: "8px" }}
        />
        <img
          src="https://picsum.photos/id/1025/300/200"
          alt="Gallery 4"
          style={{ width: "100%", height: "auto", borderRadius: "8px" }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #d9f99d, #bef264)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#365314" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Practice using <code>max-width: 100%</code> and{" "}
            <code>height: auto</code> on images.
          </li>
          <li>
            Experiment with <code>object-fit</code> for different container
            types.
          </li>
          <li>
            Use <code>srcset</code> and <code>sizes</code> for optimal image
            loading on different devices.
          </li>
          <li>
            Combine responsive images with Grid or Flexbox for galleries and
            card layouts.
          </li>
          <li>
            Test images on multiple screen sizes to ensure correct scaling and
            cropping.
          </li>
        </ul>
      </div>
    </div>
  );
}
